import { useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { adminApi, errText } from "./api";
import { Field, SaveBtn, Panel, inputCls } from "./Fields";

const blank = () => ({
  id: `p-${Date.now().toString(36)}`, tag: "", name: "", period: "12 Aylık", price: "", unit: "TL", coverage: "",
  features: [], cta: "WhatsApp ile Sipariş Ver", flag: "", featured: false, trial: false,
});

const PackageForm = ({ p, i, n, set, move, remove }) => {
  const up = (k, v) => set({ ...p, [k]: v });
  return (
    <Panel className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#5b5fd6]/20 font-display text-sm font-bold text-[#a4a6ff]">{i + 1}</span>
          <p className="font-display font-semibold truncate">{p.name || "Yeni paket"}</p>
          {p.price && <span className="hidden sm:inline shrink-0 rounded-lg bg-white/[0.06] px-2.5 py-1 text-xs font-bold">{p.price} {p.unit}</span>}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => move(-1)} disabled={i === 0} aria-label="Yukarı" data-testid={`pkg-up-${i}`} className="p-2 rounded-full hover:bg-white/10 disabled:opacity-30"><ArrowUp size={16} /></button>
          <button type="button" onClick={() => move(1)} disabled={i === n - 1} aria-label="Aşağı" data-testid={`pkg-down-${i}`} className="p-2 rounded-full hover:bg-white/10 disabled:opacity-30"><ArrowDown size={16} /></button>
          <button type="button" onClick={remove} aria-label="Sil" data-testid={`pkg-delete-${i}`} className="p-2 rounded-full text-red-300 hover:bg-red-500/15"><Trash2 size={16} /></button>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Field id={`pn-${i}`} label="Paket adı" value={p.name} required onChange={(e) => up("name", e.target.value)} data-testid={`pkg-name-${i}`} />
        <Field id={`pp-${i}`} label="Fiyat" value={p.price} required onChange={(e) => up("price", e.target.value)} data-testid={`pkg-price-${i}`} />
        <Field id={`pu-${i}`} label="Para birimi" value={p.unit} placeholder="TL, €, $" onChange={(e) => up("unit", e.target.value)} data-testid={`pkg-unit-${i}`} />
        <Field id={`pd-${i}`} label="Süre" value={p.period} placeholder="12 Aylık" onChange={(e) => up("period", e.target.value)} data-testid={`pkg-period-${i}`} />
        <Field id={`pt-${i}`} label="Etiket" value={p.tag} placeholder="Tüm Dünya" onChange={(e) => up("tag", e.target.value)} data-testid={`pkg-tag-${i}`} />
        <Field id={`pc-${i}`} label="Buton yazısı" value={p.cta} onChange={(e) => up("cta", e.target.value)} data-testid={`pkg-cta-${i}`} />
      </div>
      <Field id={`pv-${i}`} label="Kapsam açıklaması" value={p.coverage} onChange={(e) => up("coverage", e.target.value)} data-testid={`pkg-coverage-${i}`} />
      <div className="space-y-1.5">
        <Label htmlFor={`pf-${i}`} className="text-[#c9c6f0] text-xs uppercase tracking-wider">Özellikler (her satıra bir tane)</Label>
        <Textarea id={`pf-${i}`} rows={4} className={inputCls} value={(p.features || []).join("\n")}
          onChange={(e) => up("features", e.target.value.split("\n"))} data-testid={`pkg-features-${i}`} />
      </div>
      <div className="flex flex-wrap items-center gap-6">
        <label className="flex items-center gap-2 text-sm">
          Bayrak
          <select value={p.flag} onChange={(e) => up("flag", e.target.value)} data-testid={`pkg-flag-${i}`}
            className="h-10 rounded-xl bg-white/[0.04] border border-white/10 px-3 text-sm">
            <option value="">Yok</option><option value="tr">Türkiye</option><option value="eu">Avrupa</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={p.featured} onCheckedChange={(v) => up("featured", v)} data-testid={`pkg-featured-${i}`} className="data-[state=checked]:bg-[#5b5fd6] data-[state=unchecked]:bg-white/25" /> Öne çıkar</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={p.trial} onCheckedChange={(v) => up("trial", v)} data-testid={`pkg-trial-${i}`} className="data-[state=checked]:bg-[#5b5fd6] data-[state=unchecked]:bg-white/25" /> Ücretsiz test paketi</label>
      </div>
    </Panel>
  );
};

export const PackagesEditor = ({ initial, onSaved }) => {
  const [list, setList] = useState(initial);
  const [busy, setBusy] = useState(false);

  const setAt = (i, p) => setList(list.map((x, j) => (j === i ? p : x)));
  const move = (i, d) => {
    const next = [...list];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setList(next);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = list.map((p) => ({ ...p, features: (p.features || []).map((f) => f.trim()).filter(Boolean) }));
      const { data } = await adminApi.put("/admin/packages", body);
      setList(data);
      toast.success("Paketler kaydedildi");
      onSaved();
    } catch (ex) {
      toast.error(errText(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="admin-packages-form">
      {list.map((p, i) => (
        <PackageForm key={p.id} p={p} i={i} n={list.length} set={(v) => setAt(i, v)} move={(d) => move(i, d)}
          remove={() => window.confirm(`"${p.name || "Paket"}" silinsin mi?`) && setList(list.filter((_, j) => j !== i))} />
      ))}
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={() => setList([...list, blank()])} data-testid="admin-package-add"
          className="inline-flex items-center gap-2 rounded-xl border border-dashed border-white/20 bg-white/[0.02] px-6 h-11 font-semibold hover:bg-white/[0.06] hover:border-[#8a8dff]/60 transition-colors">
          <Plus size={16} /> Yeni Paket Ekle
        </button>
        <SaveBtn busy={busy} data-testid="admin-packages-save">Paketleri Kaydet</SaveBtn>
      </div>
    </form>
  );
};
